'use client';

// `<DevToolkit />` — the entry guard. Returns `null` for end users so
// the DevToolkit chunk (registry, manifest, tabs, every flag name +
// description + callsite path) never downloads to non-internal sessions.
//
// Visibility rules (the gate that decides whether to load the chunk):
//   1. `NEXT_PUBLIC_DEV_TOOLKIT=1` — explicit local/dev opt-in.
//   2. Authenticated user with `@morfi.markets` email — internal team
//      on production deployments.
//   3. Otherwise — `null`. No DOM, no chunk fetch, no leaked metadata.
//
// The old `NODE_ENV !== 'production'` gate made every local validator run load
// the debug bridge, which posts `/api/dev-internal/sdk-state` every 500ms and
// makes local network traces diverge from devnet. Keep normal local runs quiet.
//
// Why dynamic import: a static `import './dev-toolkit-internal'` at
// the top of this file would pull the registry / manifest into the
// main client bundle, defeating the gate. Webpack/Turbopack code-split
// the dynamic import into its own chunk that only fetches when the
// component renders. The shell + every dependency it brings (`@/config/
// feature-gates.ts`, `feature-gate-usages.generated.ts`, the tabs)
// stays out of the main bundle.

import { useEngineerIdentity } from '@morfi/backend-sdk-v2/client';
import dynamic from 'next/dynamic';
import type { ComponentType, JSX } from 'react';
import { env } from '@/lib/config/env';

const DEV_TOOLKIT_OPT_IN: boolean = env.NEXT_PUBLIC_DEV_TOOLKIT === '1';
const INTERNAL_EMAIL_DOMAIN = '@morfi.markets';

function isInternalEmail(email: string | null | undefined): boolean {
  return typeof email === 'string' && email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

const DevToolkitInternal: ComponentType<Record<string, never>> = dynamic(
  (): Promise<typeof import('./dev-toolkit-internal').default> =>
    import('./dev-toolkit-internal').then((m) => m.default),
  {
    ssr: false,
    loading: (): null => null,
  },
);

export function DevToolkit(): JSX.Element | null {
  // Spec 048 FR-035 — read the ENGINEER's true identity, not the
  // impersonation overlay. If an engineer activates impersonation
  // against a non-engineer target, `useAuth()` would return the
  // target's email and the gate would hide the DevToolkit, locking
  // the engineer out of the impersonate tab and Stop control.
  // `useEngineerIdentity` returns the raw auth context, bypassing
  // the overlay.
  const { user } = useEngineerIdentity();
  const visibleByEnv = DEV_TOOLKIT_OPT_IN;
  const visibleByEmail = isInternalEmail(user?.email);
  if (!visibleByEnv && !visibleByEmail) return null;
  return <DevToolkitInternal />;
}
